import { useCallback, useEffect, useRef, useState } from "react";
import {
    Loader2,
    Image as ImageIcon,
    Clapperboard,
    LogOut,
    Sparkles,
    Download,
    Lock,
} from "lucide-react";
import Seo from "../components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ez_admin_token";

const IMAGE_SIZES = [
    { value: "landscape_16_9", label: "Landscape 16:9" },
    { value: "landscape_4_3", label: "Landscape 4:3" },
    { value: "square_hd", label: "Square HD" },
    { value: "portrait_4_3", label: "Portrait 4:3" },
    { value: "portrait_16_9", label: "Portrait 16:9" },
];

function authHeaders() {
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}`,
    };
}

async function downloadFile(url, filename) {
    // Force a real device download; fall back to a new tab if CORS blocks the fetch.
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("fetch failed");
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = filename || url.split("/").pop() || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
    } catch (_) {
        window.open(url, "_blank", "noopener");
    }
}

export default function AdminStudio() {
    const [authed, setAuthed] = useState(null); // null=checking, true, false
    const [tab, setTab] = useState("image");
    const [generations, setGenerations] = useState([]);
    const [videoSeed, setVideoSeed] = useState(null); // {url, key}

    const makeVideoFrom = useCallback((url) => {
        setVideoSeed({ url, key: Date.now() });
        setTab("video");
    }, []);

    const loadGenerations = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/admin/generations`, { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setGenerations(data.items || []);
            }
        } catch (_) {}
    }, []);

    useEffect(() => {
        const check = async () => {
            const token = localStorage.getItem(TOKEN_KEY);
            if (!token) return setAuthed(false);
            try {
                const res = await fetch(`${API}/api/admin/me`, { headers: authHeaders() });
                setAuthed(res.ok);
            } catch (_) {
                setAuthed(false);
            }
        };
        check();
    }, []);

    useEffect(() => {
        if (authed) loadGenerations();
    }, [authed, loadGenerations]);

    if (authed === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#050b14]">
                <Loader2 className="w-6 h-6 text-[#00f0ff] animate-spin" />
            </div>
        );
    }

    if (!authed) {
        return <LoginGate onSuccess={() => setAuthed(true)} />;
    }

    return (
        <main data-testid="admin-studio" className="min-h-screen bg-[#050b14] pt-28 pb-20">
            <Seo title="Studio" path="/admin" description="Internal AI asset studio." />
            <div className="max-w-5xl mx-auto px-6">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                            Internal · fal.ai
                        </span>
                        <h1 className="font-heading text-3xl font-black tracking-tighter mt-1">
                            Asset Studio
                        </h1>
                    </div>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={() => {
                            localStorage.removeItem(TOKEN_KEY);
                            setAuthed(false);
                        }}
                        className="inline-flex items-center gap-2 text-sm text-[#94a3b8] hover:text-white transition-colors"
                    >
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>

                <div className="flex gap-2 mb-6">
                    <TabButton id="image" tab={tab} setTab={setTab} icon={ImageIcon} label="Image" />
                    <TabButton id="video" tab={tab} setTab={setTab} icon={Clapperboard} label="Video" />
                </div>

                {tab === "image" ? (
                    <ImagePanel onDone={loadGenerations} onMakeVideo={makeVideoFrom} />
                ) : (
                    <VideoPanel onDone={loadGenerations} seed={videoSeed} />
                )}

                <Gallery items={generations} onMakeVideo={makeVideoFrom} />
            </div>
        </main>
    );
}

function TabButton({ id, tab, setTab, icon: Icon, label }) {
    const active = tab === id;
    return (
        <button
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                active
                    ? "bg-[#00f0ff] text-[#050b14]"
                    : "bg-[#0a1325] text-[#94a3b8] border border-white/10 hover:text-white"
            }`}
        >
            <Icon className="w-4 h-4" /> {label}
        </button>
    );
}

function LoginGate({ onSuccess }) {
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });
            const data = await res.json();
            if (res.ok && data.token) {
                localStorage.setItem(TOKEN_KEY, data.token);
                onSuccess();
            } else {
                setError(typeof data.detail === "string" ? data.detail : "Login failed");
            }
        } catch (_) {
            setError("Network error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#050b14] px-6">
            <form
                data-testid="admin-login-form"
                onSubmit={submit}
                className="w-full max-w-sm rounded-2xl bg-[#0a1325] border border-white/10 p-8"
            >
                <div className="w-12 h-12 rounded-lg bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center mb-5">
                    <Lock className="w-5 h-5 text-[#00f0ff]" />
                </div>
                <h1 className="font-heading text-2xl font-bold tracking-tight">Studio access</h1>
                <p className="text-sm text-[#94a3b8] mt-2 mb-6">Enter the admin password.</p>
                <input
                    data-testid="admin-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full px-4 py-3 rounded-lg bg-[#050b14] border border-white/15 text-white placeholder-[#64748b] focus:border-[#00f0ff] outline-none transition-colors"
                />
                {error && (
                    <p data-testid="admin-login-error" className="text-sm text-red-400 mt-3">
                        {error}
                    </p>
                )}
                <button
                    data-testid="admin-login-btn"
                    type="submit"
                    disabled={loading || !password}
                    className="w-full mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold disabled:opacity-50 hover:bg-white transition-all"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter studio"}
                </button>
            </form>
        </div>
    );
}

function ImagePanel({ onDone, onMakeVideo }) {
    const [prompt, setPrompt] = useState("");
    const [size, setSize] = useState("landscape_16_9");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [url, setUrl] = useState("");

    const generate = async () => {
        setError("");
        setUrl("");
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/admin/generate/image`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ prompt, image_size: size }),
            });
            const data = await res.json();
            if (res.ok && data.url) {
                setUrl(data.url);
                onDone();
            } else {
                setError(typeof data.detail === "string" ? data.detail : "Generation failed");
            }
        } catch (_) {
            setError("Network error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rounded-2xl bg-[#0a1325] border border-white/10 p-6 sm:p-8">
            <textarea
                data-testid="image-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder="Describe the image you want to generate…"
                className="w-full px-4 py-3 rounded-lg bg-[#050b14] border border-white/15 text-white placeholder-[#64748b] focus:border-[#00f0ff] outline-none resize-none"
            />
            <div className="flex flex-wrap items-center gap-3 mt-4">
                <select
                    data-testid="image-size-select"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className="px-4 py-2.5 rounded-lg bg-[#050b14] border border-white/15 text-white text-sm outline-none focus:border-[#00f0ff]"
                >
                    {IMAGE_SIZES.map((s) => (
                        <option key={s.value} value={s.value}>
                            {s.label}
                        </option>
                    ))}
                </select>
                <button
                    data-testid="image-generate-btn"
                    onClick={generate}
                    disabled={loading || !prompt.trim()}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold disabled:opacity-50 hover:bg-white transition-all"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loading ? "Generating…" : "Generate image"}
                </button>
            </div>
            {error && <p className="text-sm text-red-400 mt-4">{error}</p>}
            {url && (
                <div data-testid="image-result" className="mt-6">
                    <img src={url} alt="Generated" className="rounded-xl border border-white/10 max-h-[420px]" />
                    <div className="flex flex-wrap gap-3 mt-4">
                        <button
                            data-testid="image-make-video-btn"
                            onClick={() => onMakeVideo(url)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold text-sm hover:bg-white transition-all"
                        >
                            <Clapperboard className="w-4 h-4" /> Make video from this
                        </button>
                        <DownloadButton url={url} filename="ezbuildabot-image.jpg" />
                    </div>
                </div>
            )}
        </div>
    );
}

function VideoPanel({ onDone, seed }) {
    const [mode, setMode] = useState(seed?.url ? "image" : "text"); // text | image
    const [prompt, setPrompt] = useState("");
    const [imageUrl, setImageUrl] = useState(seed?.url || "");
    const [duration, setDuration] = useState(5);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const pollRef = useRef(null);

    useEffect(() => () => clearInterval(pollRef.current), []);

    // React to an image handed over from the Image tab / gallery.
    useEffect(() => {
        if (seed?.url) {
            setMode("image");
            setImageUrl(seed.url);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seed?.key]);

    const poll = (requestId) => {
        pollRef.current = setInterval(async () => {
            try {
                const res = await fetch(`${API}/api/admin/generate/video/${requestId}`, {
                    headers: authHeaders(),
                });
                const data = await res.json();
                if (!res.ok) {
                    clearInterval(pollRef.current);
                    setError(typeof data.detail === "string" ? data.detail : "Status check failed");
                    setLoading(false);
                    return;
                }
                setStatus(data.status);
                if (data.status === "COMPLETED") {
                    clearInterval(pollRef.current);
                    setVideoUrl(data.url);
                    setLoading(false);
                    onDone();
                }
            } catch (_) {
                clearInterval(pollRef.current);
                setError("Network error while polling");
                setLoading(false);
            }
        }, 5000);
    };

    const generate = async () => {
        setError("");
        setVideoUrl("");
        setStatus("SUBMITTING");
        setLoading(true);
        try {
            const body = { prompt, duration };
            if (mode === "image") body.image_url = imageUrl;
            const res = await fetch(`${API}/api/admin/generate/video`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (res.ok && data.request_id) {
                setStatus("IN_QUEUE");
                poll(data.request_id);
            } else {
                setError(typeof data.detail === "string" ? data.detail : "Submission failed");
                setLoading(false);
            }
        } catch (_) {
            setError("Network error");
            setLoading(false);
        }
    };

    const canGenerate = prompt.trim() && (mode === "text" || imageUrl.trim());

    return (
        <div className="rounded-2xl bg-[#0a1325] border border-white/10 p-6 sm:p-8">
            <div className="flex gap-2 mb-4">
                {["text", "image"].map((m) => (
                    <button
                        key={m}
                        data-testid={`video-mode-${m}`}
                        onClick={() => setMode(m)}
                        className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                            mode === m
                                ? "bg-[#00f0ff]/15 text-[#00f0ff] border border-[#00f0ff]/40"
                                : "text-[#94a3b8] border border-white/10"
                        }`}
                    >
                        {m === "text" ? "Text → Video" : "Image → Video"}
                    </button>
                ))}
            </div>
            {mode === "image" && (
                <div className="mb-3">
                    <input
                        data-testid="video-image-url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        placeholder="Source image URL (e.g. paste a generated image URL)"
                        className="w-full px-4 py-3 rounded-lg bg-[#050b14] border border-white/15 text-white placeholder-[#64748b] focus:border-[#00f0ff] outline-none"
                    />
                    {imageUrl && (
                        <img
                            src={imageUrl}
                            alt="Source"
                            className="mt-3 h-24 rounded-lg border border-white/10 object-cover"
                        />
                    )}
                </div>
            )}
            <textarea
                data-testid="video-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder="Describe the motion / scene…"
                className="w-full px-4 py-3 rounded-lg bg-[#050b14] border border-white/15 text-white placeholder-[#64748b] focus:border-[#00f0ff] outline-none resize-none"
            />
            <div className="flex flex-wrap items-center gap-3 mt-4">
                <select
                    data-testid="video-duration-select"
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="px-4 py-2.5 rounded-lg bg-[#050b14] border border-white/15 text-white text-sm outline-none focus:border-[#00f0ff]"
                >
                    <option value={5}>5 seconds</option>
                    <option value={10}>10 seconds</option>
                </select>
                <button
                    data-testid="video-generate-btn"
                    onClick={generate}
                    disabled={loading || !canGenerate}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold disabled:opacity-50 hover:bg-white transition-all"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loading ? "Rendering…" : "Generate video"}
                </button>
                {loading && (
                    <span className="text-xs text-[#94a3b8]">
                        {status} · this can take 1–3 minutes
                    </span>
                )}
            </div>
            {error && <p className="text-sm text-red-400 mt-4">{error}</p>}
            {videoUrl && (
                <div data-testid="video-result" className="mt-6">
                    <video src={videoUrl} controls className="rounded-xl border border-white/10 max-h-[420px] w-full" />
                    <div className="mt-4">
                        <DownloadButton url={videoUrl} filename="ezbuildabot-video.mp4" />
                    </div>
                </div>
            )}
        </div>
    );
}

function DownloadButton({ url, filename, label = "Download" }) {
    const [busy, setBusy] = useState(false);
    return (
        <button
            data-testid="download-btn"
            onClick={async () => {
                setBusy(true);
                await downloadFile(url, filename);
                setBusy(false);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0a1325] border border-white/15 text-white text-sm hover:border-[#00f0ff] hover:text-[#00f0ff] transition-colors"
        >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {label}
        </button>
    );
}

function Gallery({ items, onMakeVideo }) {
    if (!items.length) return null;
    return (
        <div data-testid="generations-gallery" className="mt-12">
            <h2 className="font-heading text-lg font-bold tracking-tight mb-4 text-[#94a3b8]">
                Recent generations
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {items.map((g) => (
                    <div
                        key={g.id}
                        className="group relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#0a1325] flex items-center justify-center"
                        title={g.prompt}
                    >
                        {g.kind === "image" && g.url ? (
                            <img src={g.url} alt={g.prompt} className="w-full h-full object-cover" />
                        ) : g.kind === "video" && g.url ? (
                            <video src={g.url} className="w-full h-full object-cover" muted />
                        ) : (
                            <span className="text-[10px] text-[#64748b] px-2 text-center">
                                {g.kind} · {g.status}
                            </span>
                        )}

                        {g.url && (
                            <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 p-2 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                {g.kind === "image" && (
                                    <button
                                        data-testid="gallery-make-video-btn"
                                        onClick={() => onMakeVideo(g.url)}
                                        title="Make video from this image"
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#00f0ff] text-[#050b14] text-[10px] font-bold hover:bg-white transition-colors"
                                    >
                                        <Clapperboard className="w-3 h-3" /> Video
                                    </button>
                                )}
                                <button
                                    onClick={() => downloadFile(g.url, `ezbuildabot-${g.kind}`)}
                                    title="Download"
                                    className="inline-flex items-center justify-center p-1.5 rounded bg-white/15 text-white hover:bg-white/25 transition-colors"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
